/**
 * Admin mailbox CRUD + OAuth connect + manual poll (SA-92, spec §9).
 * Mirrors `backend/app/routers/mailboxes.py` / `app/schemas/mailbox.py`
 * exactly — snake_case wire shape, no client-side transformation.
 */
import { apiClient, ApiError } from './apiClient'

const BASE = '/api/mailboxes'

/** SA-402 D38: `record` binds a quote intake agenda through the SAME
 *  `sink_config.workflow_id` key the workflow sink uses. SA-716: `router`
 *  keeps a routing policy in `sink_config` (its editor is SA-717's). */
export type SinkType = 'workflow' | 'conversation' | 'record' | 'router'

/** SA-759: what runs on the earlier mail a backload loads. */
export type BackloadMode = 'capture' | 'propose' | 'live'

/** SA-759: one backload run on a mailbox. The counts are the server's; the
 *  creator's name is server-resolved (no user id is served). */
export interface MailboxBackload {
  id: string
  mode: BackloadMode
  status: 'loading' | 'done' | 'failed' | 'cancelled'
  range_start: string
  range_end: string
  recency_days: number
  total: number
  position: number
  found: number
  loaded: number
  already_present: number
  skipped: number
  parked: number
  /** SA-1009: files the run's messages could not load (absent from an older server = none). */
  files_missing?: number
  /** SA-986: the run's model calls ran ahead of its writes (absent from an older server = no). */
  classify_ahead?: boolean
  error: string | null
  created_by_name: string | null
  cancelled_by_name: string | null
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export interface BackloadRange {
  start: string
  end: string
}

export interface BackloadPreview {
  found: number
  already_present: number
  to_load: number
}

export interface BackloadStart extends BackloadRange {
  mode: BackloadMode
  recency_days?: number
  classify_ahead?: boolean
}

/** SA-947 (spec P2): the channel a source's messages carry. */
export type MailboxChannel = 'email' | 'whatsapp-group'

/** SA-947 (spec P2): a provider this deployment offers. `oauth_config_id`
 *  names the OAuth client config a provider registers; null when it has none. */
export interface MailboxProvider {
  id: string
  label: string
  channel: MailboxChannel
  push: boolean
  oauth_config_id: string | null
}

/** SA-947 (spec P3): the group a WhatsApp group source is bound to. */
export interface MailboxGroup {
  id: string
  subject: string | null
  participant_count: number
}

/** SA-943: a group source's link and binding, as the connect endpoints answer. */
export interface WahaSourceRead {
  status: Mailbox['status']
  session_status: string | null
  connected_account: string | null
  group_id: string | null
  group_subject: string | null
  participant_count: number | null
  live_since: string | null
}

/** SA-943: one of the linked account's groups. */
export interface WahaGroup {
  id: string
  subject: string
  participant_count: number
}

export interface Mailbox {
  id: string
  provider: string
  display_name: string
  email_address: string | null
  /** SA-755: the account the mailbox reads, as the provider reported it at
   *  connect (or, for a mailbox connected earlier, at a token refresh);
   *  null until then. Lowercase. */
  connected_account: string | null
  /** SA-1422: a person confirmed the typed address is served by the connected account. */
  account_confirmed?: boolean
  /** SA-1422: the two addresses differ and nobody confirmed it. Older servers send neither. */
  account_flagged?: boolean
  status: 'unconnected' | 'connecting' | 'active' | 'paused' | 'reauth_required'
  last_error: string | null
  last_polled_at: string | null
  persona_id: string | null
  sink_type: SinkType
  /** A bound sink holds `{workflow_id}`; a router holds its routing policy
   * (SA-717: edited on the Mailboxes page, validated by the server). */
  sink_config: Record<string, unknown> | null
  owner_user_id: string | null
  prefill_map: Record<string, string> | null
  connected: boolean
  outbound_enabled: boolean
  send_reconsent_needed: boolean
  /** SA-101: non-null when the bound workflow drifted — workflow_missing |
   *  workflow_archived | workflow_no_version | workflow_not_intake_agenda. */
  binding_warning: string | null
  /** SA-103: parked (dead-lettered) message stats. Computed on the list
   *  endpoint (0 / null when clean); null on mutation responses. */
  parked_count: number | null
  last_parked_at: string | null
  /** SA-128: committed intakes whose post-commit step failed — frozen
   *  failed_attempts, never parks. Same population convention. */
  post_commit_failure_count: number | null
  /** SA-717: messages waiting on a person; router mailboxes on the list
   *  endpoint only, null elsewhere. */
  triage_waiting: { awaiting_approval: number; needs_triage: number } | null
  /** SA-759: the unfinished backload run, else the latest; list only, null
   *  when the mailbox has none (and on mutation responses). */
  backload: MailboxBackload | null
  /** SA-947: the row's channel. Optional: a response from a backend older
   *  than SA-947 carries none, and reads as email. */
  channel?: MailboxChannel
  /** SA-947 (spec P3): a group source's bound group; null otherwise. */
  group?: MailboxGroup | null
  /** SA-1386: the connection's health, naive UTC. Optional: an older backend
   *  sends none. */
  failing_since?: string | null
  offline_since?: string | null
  retry_count?: number
  next_retry_at?: string | null
  notice?: string | null
}

export interface MailboxCreate {
  provider: string
  display_name: string
  email_address?: string | null
  persona_id?: string | null
  sink_type?: SinkType
  sink_config?: Record<string, unknown> | null
  owner_user_id?: string | null
  prefill_map?: Record<string, string> | null
  /** SA-943: a WhatsApp group source names the deployment's WAHA set. */
  credential_set_id?: string | null
}

/** SA-717 (spec T29): a registered email handler, as the policy editor lists
 *  it. `config` says what its `handler_config.workflow_id` picks. */
export interface MailboxHandler {
  id: string
  label: string
  config: 'agenda' | 'workflow' | 'outcomes' | null
  /** SA-945: the channels the handler serves; null or absent = every channel. */
  channels?: string[] | null
}

/** SA-717, SA-1421: one change to what a mailbox does with its mail (who,
 *  when, and the binding, owner, persona and outbound sending before and
 *  after). Names are server-resolved; no id is served. */
export interface MailboxPolicyChange {
  id: string
  changed_by_name: string | null
  changed_at: string
  /** Which of `binding`, `owner`, `persona`, `outbound` moved. Optional
   *  because a server from before SA-1421 sends none (read as the binding). */
  fields?: string[]
  sink_type_before: string | null
  sink_type_after: string
  config_before: Record<string, unknown> | null
  config_after: Record<string, unknown> | null
  owner_before_name?: string | null
  owner_after_name?: string | null
  persona_before_name?: string | null
  persona_after_name?: string | null
  outbound_enabled_before?: boolean | null
  outbound_enabled_after?: boolean | null
}

export type MailboxUpdate = Partial<Omit<MailboxCreate, 'provider'>> & {
  outbound_enabled?: boolean
  /** SA-1080: the stored policy the save was reviewed against; the server
   *  refuses 409 `policy_changed` when the stored one has moved since. */
  expected_policy?: Record<string, unknown>
}

/** SA-1080: what a save of a routing policy would store (`policy`) beside
 *  what is stored now (`current`), both normalized by the server. */
export interface PolicyPreview {
  policy: Record<string, unknown>
  current: Record<string, unknown>
}

export interface ProviderClientConfig {
  oauth_client_id: string | null
  oauth_client_secret_set: boolean
}

export interface MailboxPollResult {
  processed: number
  // SA-108: `(string & {})` keeps literal autocomplete/narrowing without
  // closing the union — a stale bundle must still type a future outcome
  // (the poll-now banner falls back to naming it).
  outcome: 'ok' | 'cursor_reanchored' | (string & {})
  detail: string | null
}

/** SA-717 (spec T24): one mailbox's triage over the last `days` days. A row
 *  counts under the kind that was PROPOSED for it. */
export interface TriageMetrics {
  days: number
  kinds: Record<string, {
    label: string
    /** Waiting for approval now, whatever its age (SA-1421). */
    proposed: number
    auto: number
    approved: number
    changed_to: Record<string, number>
    agreement_rate: number | null
  }>
  unknown: { waiting: number; resolved_to: Record<string, number> }
  calibration: Array<{ bucket: 'lt_60' | '60_75' | '75_90' | 'gte_90'; n: number; agreed: number }>
  handler_outcomes: Record<string, Record<string, number>>
  labels: Record<string, string>
}

async function errorDetail(response: Response): Promise<unknown> {
  try {
    const body = await response.json()
    return body && typeof body === 'object' && 'detail' in body ? (body as { detail: unknown }).detail : body
  } catch {
    return null
  }
}

export const mailboxesService = {
  list: () => apiClient.get<Mailbox[]>(BASE),
  create: (body: MailboxCreate) => apiClient.post<Mailbox>(BASE, body),
  update: (id: string, body: MailboxUpdate) =>
    apiClient.put<Mailbox>(`${BASE}/${id}`, body),
  handlers: () => apiClient.get<MailboxHandler[]>(`${BASE}/handlers`),
  previewPolicy: (id: string, sinkConfig: Record<string, unknown>) =>
    apiClient.post<PolicyPreview>(`${BASE}/${id}/policy-preview`, { sink_config: sinkConfig }),
  policyChanges: (id: string) => apiClient.get<MailboxPolicyChange[]>(`${BASE}/${id}/policy-changes`),
  triageMetrics: (id: string, days: 30 | 90) =>
    apiClient.get<TriageMetrics>(`${BASE}/${id}/triage-metrics?days=${days}`),
  providers: () => apiClient.get<MailboxProvider[]>(`${BASE}/providers`),
  remove: (id: string, opts?: { keepSession?: boolean }) =>
    apiClient.delete(opts?.keepSession ? `${BASE}/${id}?keep_session=true` : `${BASE}/${id}`),
  connect: (id: string, account?: string) =>
    apiClient.post<{ authorization_url?: string; status?: string }>(
      `${BASE}/${id}/connect`,
      account ? { account } : {},
    ),
  confirmAccount: (id: string, account: string) =>
    apiClient.post<Mailbox>(`${BASE}/${id}/account/confirm`, { account }),
  connectStatus: (id: string) => apiClient.get<WahaSourceRead>(`${BASE}/${id}/connect/status`),
  requestPairingCode: (id: string, phoneNumber: string) =>
    apiClient.post<{ code: string }>(`${BASE}/${id}/connect/code`, { phone_number: phoneNumber }),
  qr: async (id: string): Promise<Blob> => {
    const path = `${BASE}/${id}/connect/qr`
    const response = await apiClient.fetch(path)
    if (!response.ok) {
      throw new ApiError(`GET ${path} failed: ${response.statusText}`, response.status, await errorDetail(response))
    }
    return response.blob()
  },
  groups: (id: string) => apiClient.get<WahaGroup[]>(`${BASE}/${id}/binding/groups`),
  bind: (id: string, groupId: string) =>
    apiClient.put<WahaSourceRead>(`${BASE}/${id}/binding`, { group_id: groupId }),
  unbind: (id: string) => apiClient.delete(`${BASE}/${id}/binding`),
  pause: (id: string) => apiClient.post<Mailbox>(`${BASE}/${id}/pause`, {}),
  resume: (id: string) => apiClient.post<Mailbox>(`${BASE}/${id}/resume`, {}),
  pollNow: (id: string) =>
    apiClient.post<MailboxPollResult>(`${BASE}/${id}/poll-now`, {}),
  previewBackload: (id: string, body: BackloadRange) =>
    apiClient.post<BackloadPreview>(`${BASE}/${id}/backload/preview`, body),
  startBackload: (id: string, body: BackloadStart) =>
    apiClient.post<MailboxBackload>(`${BASE}/${id}/backloads`, body),
  cancelBackload: (id: string, jobId: string) =>
    apiClient.post<MailboxBackload>(`${BASE}/${id}/backloads/${jobId}/cancel`, {}),
  getProviderConfig: (providerConfigId: string) =>
    apiClient.get<ProviderClientConfig>(
      `/api/integrations/${providerConfigId}/provider-config`),
  putProviderConfig: (
    providerConfigId: string,
    body: { oauth_client_id: string; oauth_client_secret?: string | null },
  ) =>
    apiClient.put<ProviderClientConfig>(
      `/api/integrations/${providerConfigId}/provider-config`, body),
}
